import { defineStore } from 'pinia'

import { formatByteSize } from '@/services/formattingService'
import { generateId } from '@/services/idService'
import { dbService, type EventQueueOwnerContext } from '@/services/indexedDBService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import {
  isOfflineReadOnly,
  throwIfOfflineReadOnly,
} from '@/services/offlineModificationGuardService'
import { settingsService } from '@/services/settingsService'
import { storageQuotaService } from '@/services/storageQuotaService'
import { syncService } from '@/services/syncService'
import { showError, showInfo } from '@/services/toastService'
import { UI_SETTING_KEYS } from '@/services/uiSettingsRegistry'
import { EventType, type Event, type EventChange } from '@/types/event'
import { toPlainData } from '@/utils/plainData'

import { useWorkspaceStore } from './workspaceStore'

interface EventState {
  events: Event[]
  // Server-rejected events moved aside so the rest of the queue keeps syncing.
  // Same IndexedDB store, flagged `_quarantined`.
  quarantinedEvents: Event[]
  lastSyncTimestamp: number | null
  nextQueueOrder: number
  ownerUserId: number | null
  ownerGeneration: number
  initialized: boolean
}

function compareQueuedEvents(
  left: Event & { _originalIndex?: number },
  right: Event & { _originalIndex?: number },
): number {
  if (left.timestamp !== right.timestamp) {
    return left.timestamp - right.timestamp
  }

  if (left._queueOrder !== undefined && right._queueOrder !== undefined) {
    if (left._queueOrder !== right._queueOrder) {
      return left._queueOrder - right._queueOrder
    }
  } else if (left._queueOrder !== undefined) {
    return -1
  } else if (right._queueOrder !== undefined) {
    return 1
  }

  return (left._originalIndex ?? 0) - (right._originalIndex ?? 0)
}

function sortQueuedEvents(events: Event[]): Event[] {
  return events
    .map((event, index) => ({ ...event, _originalIndex: index }))
    .sort(compareQueuedEvents)
    .map((eventWithIndex) => {
      const normalizedEvent: Event & { _originalIndex?: number } = { ...eventWithIndex }
      delete normalizedEvent._originalIndex
      return normalizedEvent
    })
}

function getWorkspaceCreatedPayloadClientId(payload: unknown): string | null {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const payloadRecord = payload as Record<string, unknown>
  const clientId = payloadRecord.clientId ?? payloadRecord.client_id

  return typeof clientId === 'string' && clientId.trim().length > 0 ? clientId : null
}

function getInvalidLegacyWorkspaceCreateEventIds(events: Event[]): string[] {
  return events
    .filter(
      (event) =>
        event.type === EventType.WORKSPACE_CREATED &&
        !getWorkspaceCreatedPayloadClientId(event.payload),
    )
    .map((event) => event.clientId)
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function getPayloadClientId(payload: unknown): string | null {
  const payloadRecord = asRecord(payload)
  if (!payloadRecord) return null

  const clientId = payloadRecord.clientId ?? payloadRecord.client_id
  return typeof clientId === 'string' && clientId.length > 0 ? clientId : null
}

function getTourCreatedRoutePoints(payload: unknown): Record<string, unknown>[] | null {
  const payloadRecord = asRecord(payload)
  if (!payloadRecord) return null

  const routePoints = payloadRecord.routePoints ?? payloadRecord.route_points
  if (!Array.isArray(routePoints)) {
    return null
  }

  return routePoints
    .map((routePoint) => asRecord(routePoint))
    .filter((routePoint): routePoint is Record<string, unknown> => routePoint !== null)
}

function parseEwktPoint(value: string): { lat: number; lon: number } | null {
  const match = /^SRID=\d+;POINT\(\s*(-?\d+(?:\.\d+)?)\s+(-?\d+(?:\.\d+)?)\s*\)$/i.exec(value)
  if (!match) {
    return null
  }

  return {
    lon: Number(match[1]),
    lat: Number(match[2]),
  }
}

function applyRoutePointChanges(
  routePointPayload: Record<string, unknown>,
  changes: EventChange[],
): boolean {
  for (const change of changes) {
    const { field, value } = change

    switch (field) {
      case 'order':
      case 'name':
      case 'description':
        routePointPayload[field] = value ?? null
        break
      case 'trackClientId':
        if (typeof value !== 'string' || value.length === 0) {
          return false
        }
        routePointPayload.trackClientId = value
        break
      case 'location': {
        if (typeof value !== 'string') {
          return false
        }
        const point = parseEwktPoint(value)
        if (!point) {
          return false
        }
        routePointPayload.lat = point.lat
        routePointPayload.lon = point.lon
        break
      }
      default:
        return false
    }
  }

  return true
}

function mergeSegmentPayload(
  targetPayload: Record<string, unknown>,
  sourcePayload: Record<string, unknown>,
) {
  for (const [key, value] of Object.entries(sourcePayload)) {
    if (value !== undefined) {
      targetPayload[key] = toPlainData(value)
    }
  }
}

const OFFLINE_BLOCKED_EVENT_TYPES = new Set<EventType>([
  EventType.WORKSPACE_CREATED,
  EventType.WORKSPACE_UPDATED,
  EventType.WORKSPACE_DELETED,
  EventType.WORKSPACE_POI_CREATED,
  EventType.WORKSPACE_POI_UPDATED,
  EventType.WORKSPACE_POI_DELETED,
  EventType.WORKSPACE_POI_ADDED_TO_WORKSPACE,
  EventType.WORKSPACE_POI_REMOVED_FROM_WORKSPACE,
  EventType.TOUR_CREATED,
  EventType.TOUR_ADDED_TO_WORKSPACE,
  EventType.TOUR_MEMBERSHIP_UPDATED,
  EventType.TOUR_UPDATED,
  EventType.TOUR_DELETED,
  EventType.TOUR_REMOVED_FROM_WORKSPACE,
  EventType.TRACK_CREATED,
  EventType.TRACK_UPDATED,
  EventType.TRACK_DELETED,
  EventType.SEGMENT_CREATED,
  EventType.SEGMENT_UPDATED,
  EventType.SEGMENT_DELETED,
  EventType.WAYPOINT_CREATED,
  EventType.WAYPOINT_UPDATED,
  EventType.WAYPOINT_DELETED,
  EventType.ROUTE_POINT_CREATED,
  EventType.ROUTE_POINT_UPDATED,
  EventType.ROUTE_POINT_DELETED,
  EventType.PHOTO_CREATED,
  EventType.PHOTO_UPDATED,
  EventType.PHOTO_DELETED,
  EventType.WORKSPACE_COVER_PHOTO_SET,
])

function isOfflineRecordingSaveEvent(eventData: Omit<Event, 'clientId' | 'timestamp'>): boolean {
  if (eventData.type !== EventType.TOUR_CREATED) return false

  const payload = asRecord(eventData.payload)
  if (!payload) return false

  const eventFileName = payload.eventFileName ?? payload.event_file_name
  const originalFileName = payload.eventFileOriginalName ?? payload.event_file_original_name
  const sourceFilename = payload.sourceFilename ?? payload.source_filename

  return [eventFileName, originalFileName, sourceFilename].some(
    (value) => typeof value === 'string' && value === 'recording.json',
  )
}

function assertOfflineWritable(eventData: Omit<Event, 'clientId' | 'timestamp'>): void {
  if (!isOfflineReadOnly() || !OFFLINE_BLOCKED_EVENT_TYPES.has(eventData.type)) return
  if (isOfflineRecordingSaveEvent(eventData)) return

  throwIfOfflineReadOnly(`event:${eventData.type}`)
}

export const useEventStore = defineStore('event', {
  state: (): EventState => ({
    events: [],
    quarantinedEvents: [],
    lastSyncTimestamp: null,
    nextQueueOrder: 0,
    ownerUserId: null,
    ownerGeneration: 0,
    initialized: false,
  }),

  getters: {
    // All events in the store are unsynced by definition
    unsyncedEvents: (state) => state.events,

    // Get events by entity ID (tour, track, waypoint, photo)
    getEventsByEntityId: (state) => (entityId: string) => {
      return state.events.filter((event) => {
        // Check payload structure based on event type if necessary for more robustness
        // For now, keep the simple checks
        const payload = event.payload as Record<string, unknown>
        if ('id' in payload && payload.id === entityId) return true
        if ('tourId' in payload && payload.tourId === entityId) return true
        if ('trackId' in payload && payload.trackId === entityId) return true
        if ('waypointId' in payload && payload.waypointId === entityId) return true
        return false
      })
    },

    // Get events by type
    getEventsByType: (state) => (type: EventType) => {
      return state.events.filter((event) => event.type === type)
    },

    // Photo clientIds whose PHOTO_CREATED is still queued — drives the per-tile "waiting to
    // sync" badge in PhotoList.
    pendingPhotoClientIds: (state): Set<string> => {
      const ids = new Set<string>()
      for (const event of state.events) {
        if (event.type !== EventType.PHOTO_CREATED) continue
        const clientId = (event.payload as Record<string, unknown>).clientId
        if (typeof clientId === 'string') {
          ids.add(clientId)
        }
      }
      return ids
    },

    // Workspace clientIds whose WORKSPACE_CREATED is still queued (not yet on the server). Used
    // to gate direct API operations (share, visibility, soft-delete) that would 404 before the
    // collection reaches the server.
    pendingWorkspaceCreateClientIds: (state): Set<string> => {
      const ids = new Set<string>()
      for (const event of state.events) {
        if (event.type !== EventType.WORKSPACE_CREATED) continue
        const clientId = (event.payload as Record<string, unknown>).clientId
        if (typeof clientId === 'string') {
          ids.add(clientId)
        }
      }
      return ids
    },

    // Get total size of events in bytes
    totalEventSizeBytes: (state) =>
      state.events.reduce((total, event) => total + (event._sizeBytes || 0), 0),

    // Get human-readable total size
    totalEventSizeFormatted: (state) => {
      const bytes = state.events.reduce((total, event) => total + (event._sizeBytes || 0), 0)
      return formatByteSize(bytes)
    },
  },

  actions: {
    resetOwnerState(): void {
      this.ownerUserId = null
      this.ownerGeneration++
      this.initialized = false
      this.events = []
      this.quarantinedEvents = []
      this.lastSyncTimestamp = null
      this.nextQueueOrder = 0
    },

    requireOwnerContext(): EventQueueOwnerContext {
      if (this.ownerUserId === null) {
        throw new Error('Event queue is locked')
      }
      const context = {
        ownerUserId: this.ownerUserId,
        generation: this.ownerGeneration,
      }
      if (!dbService.isEventOwnerCurrent(context)) {
        throw new Error('Event queue account changed')
      }
      return context
    },

    isOwnerContextCurrent(context: EventQueueOwnerContext): boolean {
      return (
        this.ownerUserId === context.ownerUserId &&
        this.ownerGeneration === context.generation &&
        dbService.isEventOwnerCurrent(context)
      )
    },

    assertOwnerContextCurrent(context: EventQueueOwnerContext): void {
      if (!this.isOwnerContextCurrent(context)) {
        throw new Error('Event queue account changed')
      }
    },

    async activateForOwner(ownerUserId: number): Promise<void> {
      if (!Number.isSafeInteger(ownerUserId) || ownerUserId <= 0) {
        throw new Error('Invalid event queue owner')
      }
      if (
        this.initialized &&
        this.ownerUserId === ownerUserId &&
        dbService.isEventOwnerCurrent({
          ownerUserId,
          generation: this.ownerGeneration,
        })
      ) {
        return
      }

      this.resetOwnerState()

      const activation = await dbService.activateEventOwner(ownerUserId)
      if (!activation.context) throw new Error('Failed to activate event queue owner')
      this.ownerUserId = activation.context.ownerUserId
      this.ownerGeneration = activation.context.generation
      if (activation.replacedQueue) {
        settingsService.removeSetting(UI_SETTING_KEYS.syncLastEventSyncTimestamp)
        logService.warn(
          LOG_CATEGORIES.SYNC,
          'Discarded the previous or ownerless durable event queue during account activation',
        )
      }
      await this.init()
    },

    lockLocally(): void {
      dbService.lockEventOwnerLocally()
      this.resetOwnerState()
    },

    async lockForLogout(expectedOwnerUserId?: number): Promise<void> {
      const lockOwnerUserId = expectedOwnerUserId ?? this.ownerUserId ?? undefined
      this.resetOwnerState()

      const activation = await dbService.lockEventOwner(lockOwnerUserId)
      if (activation.replacedQueue) {
        settingsService.removeSetting(UI_SETTING_KEYS.syncLastEventSyncTimestamp)
        logService.warn(
          LOG_CATEGORIES.SYNC,
          'Discarded an ownerless durable event queue while locking signed-out state',
        )
      }
    },

    async clearOwnedEvents(): Promise<void> {
      // Called unconditionally before loading any workspace (incl. anonymous viewing of a
      // public collection), so a signed-out session — no owner ever activated — has nothing
      // durable to clear. Only reset in-memory state; requireOwnerContext() would otherwise
      // throw "Event queue is locked" for a case that isn't actually an error.
      if (this.ownerUserId === null) {
        this.events = []
        this.quarantinedEvents = []
        this.lastSyncTimestamp = null
        this.nextQueueOrder = 0
        return
      }
      const context = this.requireOwnerContext()
      await dbService.clearEvents(context)
      this.events = []
      this.quarantinedEvents = []
      this.lastSyncTimestamp = null
      this.nextQueueOrder = 0
      settingsService.removeSetting(UI_SETTING_KEYS.syncLastEventSyncTimestamp)
    },

    async runInSyncBatch<T>(operation: () => Promise<T>): Promise<T> {
      return syncService.runWithEventBatch(operation)
    },

    async persistEventUpdate(event: Event) {
      const context = this.requireOwnerContext()
      event._sizeBytes = this.calculateEventSize(event)
      await dbService.saveEvent(toPlainData(event), context)
    },

    async removeQueuedEventIds(eventIds: string[]) {
      if (eventIds.length === 0) {
        return
      }

      const context = this.requireOwnerContext()
      await dbService.deleteEvents(eventIds, context)
      const eventIdSet = new Set(eventIds)
      this.events = this.events.filter((event) => !eventIdSet.has(event.clientId))
    },

    async absorbRoutePointUpdate(
      routePointClientId: string,
      changes: EventChange[],
    ): Promise<boolean> {
      if (changes.length === 0) {
        return true
      }

      const pendingRoutePointCreate = this.events.find(
        (event) =>
          event.type === EventType.ROUTE_POINT_CREATED &&
          getPayloadClientId(event.payload) === routePointClientId,
      )

      if (pendingRoutePointCreate) {
        const updatedEvent = toPlainData(pendingRoutePointCreate)
        const payload = asRecord(updatedEvent.payload)
        if (!payload || !applyRoutePointChanges(payload, changes)) {
          return false
        }

        const supersededUpdateIds = this.events
          .filter(
            (event) =>
              event.type === EventType.ROUTE_POINT_UPDATED &&
              getPayloadClientId(event.payload) === routePointClientId,
          )
          .map((event) => event.clientId)

        await this.persistEventUpdate(updatedEvent)
        Object.assign(pendingRoutePointCreate, updatedEvent)
        await this.removeQueuedEventIds(supersededUpdateIds)
        return true
      }

      for (const event of this.events) {
        if (event.type !== EventType.TOUR_CREATED) {
          continue
        }

        const routePoint = getTourCreatedRoutePoints(event.payload)?.find(
          (pendingRoutePoint) => getPayloadClientId(pendingRoutePoint) === routePointClientId,
        )
        if (!routePoint) {
          continue
        }

        const updatedEvent = toPlainData(event)
        const updatedRoutePoint = getTourCreatedRoutePoints(updatedEvent.payload)?.find(
          (pendingRoutePoint) => getPayloadClientId(pendingRoutePoint) === routePointClientId,
        )
        if (!updatedRoutePoint || !applyRoutePointChanges(updatedRoutePoint, changes)) {
          continue
        }

        const supersededUpdateIds = this.events
          .filter(
            (queuedEvent) =>
              queuedEvent.type === EventType.ROUTE_POINT_UPDATED &&
              getPayloadClientId(queuedEvent.payload) === routePointClientId,
          )
          .map((queuedEvent) => queuedEvent.clientId)

        await this.persistEventUpdate(updatedEvent)
        Object.assign(event, updatedEvent)
        await this.removeQueuedEventIds(supersededUpdateIds)
        return true
      }

      return false
    },

    async dropPendingRoutePoint(routePointClientId: string): Promise<boolean> {
      const routePointLifecycleEventIds = this.events
        .filter(
          (event) =>
            (event.type === EventType.ROUTE_POINT_CREATED ||
              event.type === EventType.ROUTE_POINT_UPDATED ||
              event.type === EventType.ROUTE_POINT_DELETED) &&
            getPayloadClientId(event.payload) === routePointClientId,
        )
        .map((event) => event.clientId)

      const pendingTourCreate = this.events.find((event) => {
        if (event.type !== EventType.TOUR_CREATED) {
          return false
        }

        const routePoints = getTourCreatedRoutePoints(event.payload)
        return (
          routePoints?.some(
            (pendingRoutePoint) => getPayloadClientId(pendingRoutePoint) === routePointClientId,
          ) ?? false
        )
      })

      if (!pendingTourCreate && routePointLifecycleEventIds.length === 0) {
        return false
      }

      if (pendingTourCreate) {
        const updatedEvent = toPlainData(pendingTourCreate)
        const payload = asRecord(updatedEvent.payload)
        const routePoints = getTourCreatedRoutePoints(updatedEvent.payload)

        if (payload && routePoints) {
          const filteredRoutePoints = routePoints.filter(
            (routePoint) => getPayloadClientId(routePoint) !== routePointClientId,
          )

          if (Array.isArray(payload.routePoints)) {
            payload.routePoints = filteredRoutePoints
          }
          if (Array.isArray(payload.route_points)) {
            payload.route_points = filteredRoutePoints
          }

          await this.persistEventUpdate(updatedEvent)
          Object.assign(pendingTourCreate, updatedEvent)
        }
      }

      await this.removeQueuedEventIds(routePointLifecycleEventIds)
      return true
    },

    async absorbSegmentUpdate(
      segmentClientId: string,
      updatePayload: unknown,
    ): Promise<Event | null> {
      const pendingSegmentCreate = this.events.find(
        (event) =>
          event.type === EventType.SEGMENT_CREATED &&
          getPayloadClientId(event.payload) === segmentClientId,
      )
      if (!pendingSegmentCreate) {
        return null
      }

      const updatedEvent = toPlainData(pendingSegmentCreate)
      const createPayload = asRecord(updatedEvent.payload)
      const updatePayloadRecord = asRecord(updatePayload)
      if (!createPayload || !updatePayloadRecord) {
        return null
      }

      mergeSegmentPayload(createPayload, updatePayloadRecord)

      const supersededUpdateIds = this.events
        .filter(
          (event) =>
            event.type === EventType.SEGMENT_UPDATED &&
            getPayloadClientId(event.payload) === segmentClientId,
        )
        .map((event) => event.clientId)

      await this.persistEventUpdate(updatedEvent)
      Object.assign(pendingSegmentCreate, updatedEvent)
      await this.removeQueuedEventIds(supersededUpdateIds)
      return pendingSegmentCreate
    },

    async foldPendingSegmentUpdatesIntoCreate(
      segmentPayload: Record<string, unknown>,
    ): Promise<string[]> {
      const segmentClientId = getPayloadClientId(segmentPayload)
      if (!segmentClientId) {
        return []
      }

      const pendingSegmentUpdates = this.events.filter(
        (event) =>
          event.type === EventType.SEGMENT_UPDATED &&
          getPayloadClientId(event.payload) === segmentClientId,
      )

      for (const pendingSegmentUpdate of pendingSegmentUpdates) {
        const updatePayload = asRecord(pendingSegmentUpdate.payload)
        if (!updatePayload) {
          continue
        }
        mergeSegmentPayload(segmentPayload, updatePayload)
      }

      return pendingSegmentUpdates.map((event) => event.clientId)
    },

    async queueRoutePointUpdate(routePointClientId: string, changes: EventChange[]) {
      const absorbed = await this.absorbRoutePointUpdate(routePointClientId, changes)
      if (absorbed) {
        return null
      }

      return this.addEvent({
        type: EventType.ROUTE_POINT_UPDATED,
        payload: {
          clientId: routePointClientId,
          changes,
        },
      })
    },

    async queueRoutePointDelete(routePointClientId: string) {
      const droppedPending = await this.dropPendingRoutePoint(routePointClientId)
      if (droppedPending) {
        return null
      }

      return this.addEvent({
        type: EventType.ROUTE_POINT_DELETED,
        payload: {
          clientId: routePointClientId,
        },
      })
    },

    /**
     * Initialize the store and load events from IndexedDB
     */
    async init() {
      if (this.initialized || this.ownerUserId === null) return
      const context = this.requireOwnerContext()
      try {
        // Load events from IndexedDB — quarantined events live in the same store, flagged
        this.events = []
        this.quarantinedEvents = []
        const eventsFromDb = await dbService.getAllEvents(context)
        if (eventsFromDb.length > 0) {
          const sorted = sortQueuedEvents(eventsFromDb)
          this.quarantinedEvents = sorted.filter((event) => event._quarantined)
          this.events = sorted.filter((event) => !event._quarantined)
          await this.pruneInvalidLegacyEvents()
        }

        const maxQueueOrder = this.events.reduce(
          (max, event) =>
            typeof event._queueOrder === 'number' ? Math.max(max, event._queueOrder) : max,
          -1,
        )
        this.nextQueueOrder = Math.max(this.events.length - 1, maxQueueOrder) + 1

        // Load last sync timestamp from settings
        const lastSyncTimestamp = settingsService.getSetting<number>(
          UI_SETTING_KEYS.syncLastEventSyncTimestamp,
        )
        if (lastSyncTimestamp) {
          this.lastSyncTimestamp = lastSyncTimestamp
        }
        this.initialized = true
      } catch (error) {
        if (!this.isOwnerContextCurrent(context)) throw error
        this.resetOwnerState()
        try {
          await dbService.lockEventOwner(context.ownerUserId)
        } catch (lockError) {
          logService.error(
            LOG_CATEGORIES.SYNC,
            'Failed to lock event queue after initialization failed',
            { error, lockError },
          )
          throw new Error(
            `Failed to initialize and lock the event queue: ${error instanceof Error ? error.message : String(error)}; ${lockError instanceof Error ? lockError.message : String(lockError)}`,
          )
        }
        showError(
          `Failed to initialize event store: ${error instanceof Error ? error.message : String(error)}`,
        )
        throw error
      }
    },

    async pruneInvalidLegacyEvents(notify: boolean = false) {
      const context = this.requireOwnerContext()
      const invalidEventIds = getInvalidLegacyWorkspaceCreateEventIds(this.events)

      if (invalidEventIds.length === 0) {
        return 0
      }

      const invalidEventIdSet = new Set(invalidEventIds)
      await dbService.deleteEvents(invalidEventIds, context)
      this.events = this.events.filter((event) => !invalidEventIdSet.has(event.clientId))

      logService.warn(LOG_CATEGORIES.SYNC, 'Discarded invalid queued workspace create events', {
        count: invalidEventIds.length,
        eventIds: invalidEventIds,
      })

      if (notify) {
        showInfo(
          `Discarded ${invalidEventIds.length} invalid queued workspace event${invalidEventIds.length === 1 ? '' : 's'} from an older session.`,
        )
      }

      return invalidEventIds.length
    },

    /**
     * Add a new event to the store. Requires a fully formed object.
     */
    async addEvent(eventData: Omit<Event, 'clientId' | 'timestamp'>) {
      assertOfflineWritable(eventData)
      const context = this.requireOwnerContext()

      const now = Date.now()
      let supersededEventIds: string[] = []

      if (eventData.type === EventType.SEGMENT_UPDATED) {
        const segmentClientId = getPayloadClientId(eventData.payload)
        if (segmentClientId) {
          const absorbedSegment = await this.absorbSegmentUpdate(segmentClientId, eventData.payload)
          if (absorbedSegment) {
            return absorbedSegment
          }
        }
      }

      if (eventData.type === EventType.SEGMENT_CREATED) {
        const segmentPayload = asRecord(eventData.payload)
        if (segmentPayload) {
          supersededEventIds = await this.foldPendingSegmentUpdatesIntoCreate(segmentPayload)
        }
      }

      // Check if this event references a project and if a PROJECT_CREATED event needs to be pushed first
      const workspaceStore = useWorkspaceStore()

      // Only check the persistent flag - don't check local unsynced events as they get removed after sync
      // This prevents duplicate PROJECT_CREATED events from being created
      if (!workspaceStore._createEventPushed && !!workspaceStore.clientId) {
        // Set the flag immediately to prevent race conditions
        workspaceStore._createEventPushed = true

        try {
          // We need to create a PROJECT_CREATED event first
          const projectCreatedPayload = {
            clientId: workspaceStore.clientId,
            name: workspaceStore.name || '',
            isPublic: workspaceStore.isPublic || false,
            markedForDeletion: workspaceStore.markedForDeletion || false,
            deletionScheduledAt: workspaceStore.deletionScheduledAt || null,
            canEdit: true,
          }

          const projectCreatedEvent: Event = {
            type: EventType.WORKSPACE_CREATED,
            clientId: generateId(),
            timestamp: now,
            _queueOrder: this.nextQueueOrder++,
            payload: toPlainData(projectCreatedPayload),
          }

          // Calculate and set the size in bytes
          projectCreatedEvent._sizeBytes = this.calculateEventSize(projectCreatedEvent)

          // Check storage quota before saving the PROJECT_CREATED event
          const canProceed = await storageQuotaService.checkQuotaBeforeOperation(
            projectCreatedEvent._sizeBytes,
          )
          if (!canProceed) {
            throw new Error('QUOTA_CHECK_CANCELLED')
          }

          // Save the PROJECT_CREATED event
          this.assertOwnerContextCurrent(context)
          await dbService.saveEvent(projectCreatedEvent, context)
          this.events.push(projectCreatedEvent)

          await workspaceStore.saveWorkspaceToDb()
        } catch (error) {
          // Reset the flag if PROJECT_CREATED event creation failed
          workspaceStore._createEventPushed = false
          throw error
        }
      }

      // Convert event payload to snake_case for API compatibility
      const event: Event = {
        ...eventData,
        clientId: generateId(),
        timestamp: now,
        _queueOrder: this.nextQueueOrder++,
        payload: toPlainData(eventData.payload),
      } as Event

      // Calculate and set the size in bytes
      event._sizeBytes = this.calculateEventSize(event)

      // Check storage quota before saving the event
      const canProceed = await storageQuotaService.checkQuotaBeforeOperation(event._sizeBytes)
      if (!canProceed) {
        throw new Error('QUOTA_CHECK_CANCELLED')
      }

      if (event.type === EventType.WORKSPACE_COVER_PHOTO_SET) {
        logService.info(
          LOG_CATEGORIES.SYNC,
          '[addEvent] Pushing WORKSPACE_COVER_PHOTO_SET to events',
          {
            queueOrder: event._queueOrder,
            eventsLengthBefore: this.events.length,
          },
        )
      }
      this.assertOwnerContextCurrent(context)
      await dbService.saveEvent(event, context)
      this.events.push(event)
      if (event.type === EventType.WORKSPACE_COVER_PHOTO_SET) {
        logService.info(
          LOG_CATEGORIES.SYNC,
          '[addEvent] WORKSPACE_COVER_PHOTO_SET saved to IndexedDB',
          {
            queueOrder: event._queueOrder,
            eventsLengthAfter: this.events.length,
          },
        )
      }
      await this.removeQueuedEventIds(supersededEventIds)

      // Always schedule a sync operation with delay when events are available
      if (navigator.onLine && this.isOwnerContextCurrent(context)) {
        syncService.scheduleSync()
      }

      return event
    },

    /**
     * Add multiple events to the store in bulk. Sync is scheduled only once after all events are saved.
     */
    async addEvents(eventsData: Omit<Event, 'clientId' | 'timestamp'>[]) {
      return this.runInSyncBatch(async () => {
        const context = this.requireOwnerContext()
        for (const eventData of eventsData) {
          assertOfflineWritable(eventData)
        }

        const results: Event[] = []

        for (const eventData of eventsData) {
          const now = Date.now()
          const workspaceStore = useWorkspaceStore()

          if (!workspaceStore._createEventPushed && !!workspaceStore.clientId) {
            workspaceStore._createEventPushed = true

            try {
              const projectCreatedPayload = {
                clientId: workspaceStore.clientId,
                name: workspaceStore.name || '',
                isPublic: workspaceStore.isPublic || false,
                markedForDeletion: workspaceStore.markedForDeletion || false,
                deletionScheduledAt: workspaceStore.deletionScheduledAt || null,
                canEdit: true,
              }

              const projectCreatedEvent: Event = {
                type: EventType.WORKSPACE_CREATED,
                clientId: generateId(),
                timestamp: now,
                _queueOrder: this.nextQueueOrder++,
                payload: toPlainData(projectCreatedPayload),
              }

              projectCreatedEvent._sizeBytes = this.calculateEventSize(projectCreatedEvent)
              const canProceed = await storageQuotaService.checkQuotaBeforeOperation(
                projectCreatedEvent._sizeBytes,
              )
              if (!canProceed) {
                throw new Error('QUOTA_CHECK_CANCELLED')
              }

              this.assertOwnerContextCurrent(context)
              await dbService.saveEvent(projectCreatedEvent, context)
              this.events.push(projectCreatedEvent)
              await workspaceStore.saveWorkspaceToDb()
            } catch (error) {
              workspaceStore._createEventPushed = false
              throw error
            }
          }

          const event: Event = {
            ...eventData,
            clientId: generateId(),
            timestamp: now,
            _queueOrder: this.nextQueueOrder++,
            payload: toPlainData(eventData.payload),
          } as Event

          event._sizeBytes = this.calculateEventSize(event)
          const canProceed = await storageQuotaService.checkQuotaBeforeOperation(event._sizeBytes)
          if (!canProceed) {
            throw new Error('QUOTA_CHECK_CANCELLED')
          }

          this.assertOwnerContextCurrent(context)
          await dbService.saveEvent(event, context)
          this.events.push(event)
          results.push(event)
        }

        // Schedule sync once after all events are saved
        if (navigator.onLine && results.length > 0 && this.isOwnerContextCurrent(context)) {
          syncService.scheduleSync()
        }

        return results
      })
    },

    /**
     * Remove events from the store (useful after syncing)
     */
    async removeEvents(eventIds: string[]) {
      const context = this.requireOwnerContext()
      await dbService.deleteEvents(eventIds, context)
      this.events = this.events.filter((event) => !eventIds.includes(event.clientId))
    },

    /**
     * Move server-rejected events aside so the rest of the queue keeps syncing.
     * Persisted via the `_quarantined` flag in the same IndexedDB store —
     * survives reloads.
     */
    async quarantineEvents(eventIds: string[], reason: string) {
      const context = this.requireOwnerContext()
      const ids = new Set(eventIds)
      const moved = this.events
        .filter((event) => ids.has(event.clientId))
        .map((event) => ({
          ...toPlainData(event),
          _quarantined: true,
          _quarantineReason: reason,
        }))
      if (moved.length === 0) return

      await dbService.saveEvents(moved, context)
      this.assertOwnerContextCurrent(context)
      this.events = this.events.filter((event) => !ids.has(event.clientId))
      this.quarantinedEvents = sortQueuedEvents([...this.quarantinedEvents, ...moved])
      logService.warn(LOG_CATEGORIES.SYNC, `Quarantined ${moved.length} rejected event(s)`, {
        eventIds: moved.map((event) => event.clientId),
        reason,
      })
    },

    /**
     * Put quarantined events back into the queue for another attempt. Original
     * `_queueOrder` is preserved so parents still precede their children.
     */
    async retryQuarantinedEvents(eventIds: string[]) {
      const context = this.requireOwnerContext()
      const ids = new Set(eventIds)
      const moved = this.quarantinedEvents
        .filter((event) => ids.has(event.clientId))
        .map((event) => {
          const queuedEvent = toPlainData(event)
          delete queuedEvent._quarantined
          delete queuedEvent._quarantineReason
          return queuedEvent
        })
      if (moved.length === 0) return

      await dbService.saveEvents(moved, context)
      this.assertOwnerContextCurrent(context)
      this.quarantinedEvents = this.quarantinedEvents.filter((event) => !ids.has(event.clientId))
      this.events = sortQueuedEvents([...this.events, ...moved])
    },

    /**
     * Permanently drop quarantined events from this device. Callers own rolling back the
     * optimistic local state (e.g. reloading the collection from the server).
     */
    async discardQuarantinedEvents(eventIds: string[]) {
      const context = this.requireOwnerContext()
      const ids = new Set(eventIds)
      await dbService.deleteEvents(eventIds, context)
      this.quarantinedEvents = this.quarantinedEvents.filter((event) => !ids.has(event.clientId))
      logService.info(LOG_CATEGORIES.SYNC, `Discarded ${eventIds.length} quarantined event(s)`, {
        eventIds,
      })
    },

    /**
     * Update the last sync timestamp
     */
    async updateLastSyncTimestamp(timestamp: number) {
      this.requireOwnerContext()
      this.lastSyncTimestamp = timestamp

      // Save to localStorage
      settingsService.saveSetting(UI_SETTING_KEYS.syncLastEventSyncTimestamp, timestamp)
    },

    /**
     * Calculate the size of an event in bytes by serializing to JSON
     * and accounting for binary data sizes
     */
    calculateEventSize(event: Event): number {
      try {
        // Track total size of binary data
        let binaryDataSize = 0

        // Create a cleaned version of the event to simulate storage format
        const cleanEvent = JSON.parse(
          JSON.stringify(event, (key, value) => {
            // Skip internal properties
            if (key.startsWith('_')) {
              return undefined
            }

            // Handle File and Blob objects by tracking their size separately
            if (value instanceof File || value instanceof Blob) {
              binaryDataSize += value.size
              // Replace with a placeholder in the JSON
              return `[Binary data placeholder]`
            }

            // Handle File-like objects that might be serialized (with _type property)
            if (
              typeof value === 'object' &&
              value !== null &&
              '_type' in value &&
              (value as { _type: string })._type === 'File' &&
              'size' in value
            ) {
              binaryDataSize += (value as { size: number }).size
              return `[Binary data placeholder]`
            }

            return value
          }),
        )

        // Convert to JSON string to get its size
        const jsonString = JSON.stringify(cleanEvent)
        const jsonSize = new TextEncoder().encode(jsonString).length

        // Return total size (JSON representation + binary data size)
        return jsonSize + binaryDataSize
      } catch (error) {
        logService.error(LOG_CATEGORIES.GENERAL, 'Error calculating event size', { error })
        return 0
      }
    },
  },
})
